import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Eye, Calendar, Clock, Users, Play, Star, BookOpen } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface ShadowExperience {
  id: string;
  founderName: string;
  company: string;
  industry: string;
  stage: string;
  experience: string;
  duration: string;
  date: string;
  spots: number;
  description: string;
  topics: string[];
  status: 'upcoming' | 'live' | 'completed';
  rating?: number;
  enrolled: boolean;
}

const ShadowFounderExperience: React.FC = () => {
  const { toast } = useToast();
  const [experiences, setExperiences] = useState<ShadowExperience[]>([]);
  const [selectedTab, setSelectedTab] = useState<'available' | 'enrolled' | 'completed'>('available');

  const enrollInExperience = (experienceId: string) => {
    setExperiences(experiences.map(exp => 
      exp.id === experienceId ? { ...exp, enrolled: true, spots: exp.spots - 1 } : exp
    ));
    toast({
      title: "Enrolled Successfully!",
      description: "You've been enrolled in the shadow founder experience."
    });
  };

  const cancelEnrollment = (experienceId: string) => {
    setExperiences(experiences.map(exp => 
      exp.id === experienceId ? { ...exp, enrolled: false, spots: exp.spots + 1 } : exp
    ));
    toast({
      title: "Enrollment Cancelled",
      description: "Your enrollment has been cancelled."
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800';
      case 'live': return 'bg-green-100 text-green-800';
      case 'completed': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getFilteredExperiences = () => {
    switch (selectedTab) {
      case 'enrolled':
        return experiences.filter(exp => exp.enrolled && exp.status !== 'completed');
      case 'completed':
        return experiences.filter(exp => exp.enrolled && exp.status === 'completed');
      default:
        return experiences.filter(exp => exp.status !== 'completed');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-yellow-500" />
            Shadow a Founder – Virtual Experience
          </CardTitle>
          <CardDescription>
            Get an inside look at the day-to-day life of successful entrepreneurs and startup founders.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Tab Navigation */}
          <div className="flex space-x-2 border-b">
            <button
              onClick={() => setSelectedTab('available')}
              className={`pb-2 px-1 ${selectedTab === 'available' ? 'border-b-2 border-yellow-500 text-yellow-600' : 'text-muted-foreground'}`}
            >
              Available Experiences
            </button>
            <button
              onClick={() => setSelectedTab('enrolled')}
              className={`pb-2 px-1 ${selectedTab === 'enrolled' ? 'border-b-2 border-yellow-500 text-yellow-600' : 'text-muted-foreground'}`}
            >
              My Enrollments
            </button>
            <button
              onClick={() => setSelectedTab('completed')}
              className={`pb-2 px-1 ${selectedTab === 'completed' ? 'border-b-2 border-yellow-500 text-yellow-600' : 'text-muted-foreground'}`}
            >
              Completed
            </button>
          </div>

          {/* Experience Cards */}
          <div className="space-y-4">
            {getFilteredExperiences().length === 0 ? (
              <div className="text-center py-12">
                <Eye className="h-16 w-16 mx-auto mb-4 text-yellow-500" />
                <h3 className="text-xl font-semibold mb-2">
                  {selectedTab === 'enrolled' ? 'No Enrolled Experiences' :
                   selectedTab === 'completed' ? 'No Completed Experiences' :
                   'No Experiences Available'}
                </h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {selectedTab === 'enrolled' ? 'You haven\'t enrolled in any shadow experiences yet.' :
                   selectedTab === 'completed' ? 'You haven\'t completed any shadow experiences yet.' :
                   'Shadow founder experiences will be available soon. Get an exclusive behind-the-scenes look at successful entrepreneurs.'}
                </p>
                <Button className="bg-yellow-500 hover:bg-yellow-600">
                  Get Notified When Available
                </Button>
              </div>
            ) : (
              getFilteredExperiences().map((experience) => (
                <Card key={experience.id} className="border-l-4 border-l-yellow-500 hover:shadow-md transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-xl font-semibold">{experience.founderName}</h3>
                        <p className="text-lg text-muted-foreground">{experience.company}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="outline">{experience.industry}</Badge>
                          <Badge variant="outline">{experience.stage}</Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className={getStatusColor(experience.status)}>
                          {experience.status}
                        </Badge>
                        {experience.rating && (
                          <div className="flex items-center gap-1 mt-1">
                            <Star className="h-4 w-4 text-yellow-500" />
                            <span className="text-sm">{experience.rating}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <p className="text-muted-foreground mb-4">{experience.description}</p>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{experience.date}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{experience.duration}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">{experience.spots} spots left</span>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h4 className="font-medium mb-2">What You'll Experience:</h4>
                      <div className="flex flex-wrap gap-2">
                        {experience.topics.map((topic, index) => (
                          <Badge key={index} variant="outline" className="text-xs">
                            {topic}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      {experience.status === 'live' && experience.enrolled && (
                        <Button className="bg-green-500 hover:bg-green-600">
                          <Play className="h-4 w-4 mr-2" />
                          Join Live Session
                        </Button>
                      )}
                      
                      {experience.status === 'upcoming' && (
                        experience.enrolled ? (
                          <Button 
                            variant="outline"
                            onClick={() => cancelEnrollment(experience.id)}
                            className="border-red-500 text-red-500 hover:bg-red-50"
                          >
                            Cancel Enrollment
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => enrollInExperience(experience.id)}
                            className="bg-yellow-500 hover:bg-yellow-600"
                            disabled={experience.spots === 0}
                          >
                            {experience.spots === 0 ? 'Fully Booked' : 'Enroll Now'}
                          </Button>
                        )
                      )}

                      {experience.status === 'completed' && experience.enrolled && (
                        <Button variant="outline">
                          <BookOpen className="h-4 w-4 mr-2" />
                          View Summary
                        </Button>
                      )}

                      <Button variant="outline">
                        Learn More
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShadowFounderExperience;
